import Measure from 'react-measure';
import React, { Component, PropTypes } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';

import { NitroIcon, NitroHeading, NitroDivider } from 'nitro-components';
import FilterDrawer from './FilterDrawer';
import FilterItem from './FilterItem';

import styled from 'styled-components';
import DisableCover from '../libs/disabled';

import { getFilterCountries } from '../../utils/globalSelectors';
import { setDefaultSource } from '../../utils/helpers/costcenter';
import CostcentreApi from '../../api/CostcentreApi';
import ChipInput from './filters/chip-input/ChipInput';

import * as globalActions from '../../action/CommonActions';
import { pushGAAction } from '../../utils/gtm';

const FilterGroupBox = styled.section`
  position: relative;
  display: inline-block;
  ${'' /* max-width: 48%; */} background: ${props => props.theme.color.white};
  box-shadow: ${props => (props.flat ? '' : props.theme.boxShadow.filter)};
  border-radius: 4px;
  ${'' /* overflow: hidden; */}
  padding: ${props => (props.flat ? '7px 0' : '7px')};
  max-width : ${props => (props.width ? props.width : '100%')};
  ${props =>
    props.align &&
    `
    ${props.flat ? 'position: relative' : 'position: absolute'};
    top: 0;
    ${props.align}: 0;
  `};
`;

const FilterGroupContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;
  cursor: pointer;
  align-items: center;
  height: 100%;
`;

const FilterGroupHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${props => props.theme.color.secondary};
  font-family: ${props => props.theme.font.bold};
`;

const PillContainer = styled.div`
  ${'' /* overflow: hidden; */} margin-left: 18px;
  margin-bottom: -10px;
`;
const GroupPills = styled.div`
  ${'' /* width: 10000%; */};
`;
const PillsList = styled.div`
  display: inline-block;
`;

const ShowMore = styled.div`
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  background: ${props => props.theme.color.white};
  height: 40px;
  line-height: 40px;
  border-left: 1px solid ${props => props.theme.color.border};
  box-shadow: ${props => props.theme.boxShadow.filter};
`;

const DrawerWrapper = styled.section`
  position: -ms-page;
`;

class FilterGroup extends Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      listDimensions: {
        width: -1,
        height: -1,
      },
      containerDimensions: {
        width: -1,
        height: -1,
      },
      dynamicFilterOpts: [],
    };
  }

  open = () => {
    if (this.props.disabled) return;

    pushGAAction('FILTER', `FilterDrawerOpen::${this.props.title}`);

    this.setState({
      open: true,
    });
  };

  closeFilters = () => {
    this.setState({ open: false });
  };

  applyFilters = (filters, dynFilters) => {
    this.closeFilters();
    this.props.action(filters, dynFilters);
  };

  render() {
    const {
      title,
      icon,
      filters,
      align,
      disabled,
      extraAction,
      extraSection,
      sectionTitle,
      validator,
      actions,
      companyCode,
      dynamicFilters,
      width,
      flat
    } = this.props;

    // Hardcode changes to fix costcenter confusion - NITRO-423
    if (filters && filters[1]) filters[1].title = 'Company code';

    const showMore =
      this.state.listDimensions.width >
      this.state.containerDimensions.width - 10;

    let globalDynFilter = [];
    if (dynamicFilters) {
      dynamicFilters.forEach(item => {
        if (item.value.length === 0) return false;

        globalDynFilter.push({
          title: item.title,
          label: item.value.map(val => val.label).join(' , '),
          value: item.value,
        });
      });
    }

    // only display the additional custom filters if used to show Source
    // add this special rule to backward support insight details chart filter
    const displayFilters =
      title === 'Source' ? [...filters, ...globalDynFilter] : [...filters];

    return (
      <div>
        <FilterGroupBox align={align} width={width} flat={flat}>
          <FilterGroupContainer onClick={this.open}>
            <FilterGroupHeader>
              <NitroIcon
                name={icon}
                size={16}
                variant="secondary"
                left={10}
                right={10}
              />
              <span>{title}</span>
            </FilterGroupHeader>

            <Measure
              onMeasure={dimensions => {
                this.setState({ containerDimensions: dimensions });
              }}
            >
              <PillContainer>
                <GroupPills>
                  <Measure
                    onMeasure={dimensions => {
                      this.setState({ listDimensions: dimensions });
                    }}
                  >
                    <PillsList>
                      {displayFilters.map((filter, index) => (
                        <FilterItem key={index} filter={filter} />
                      ))}
                    </PillsList>
                  </Measure>
                </GroupPills>
              </PillContainer>
            </Measure>
          </FilterGroupContainer>
          {disabled && <DisableCover />}
          {showMore && (
            <ShowMore>
              <NitroIcon name="overflow" size={16} left={10} right={10} />
            </ShowMore>
          )}

          <DrawerWrapper>
            <FilterDrawer
              actions={actions}
              validator={validator}
              extraAction={extraAction}
              sectionTitle={sectionTitle}
              extraSection={extraSection}
              action={this.applyFilters}
              open={this.state.open}
              close={this.closeFilters}
              title={title}
              filters={filters}
            />
          </DrawerWrapper>
        </FilterGroupBox>
      </div>
    );
  }
}

FilterGroup.propTypes = {
  disabled: PropTypes.bool, // Cover the filter with disable-cover
  align: PropTypes.string, // Old alignment solution
  icon: PropTypes.string, // Icon to display before filter
  title: PropTypes.string, //
  action: PropTypes.func, // Function to pass the filter array to its parent
  filters: PropTypes.array.isRequired, // Filters array with filter object
  extraAction: PropTypes.object, // Extra action button with label and action
  sectionTitle: PropTypes.string,
  extraSection: PropTypes.object,
  validator: PropTypes.func,
  actions: PropTypes.object,
  companyCode: PropTypes.object,
  dynamicFilters: PropTypes.array,
  width: PropTypes.string,
  flat: PropTypes.bool
};

const getCostCenterSelector = state => state.common.costcenter;
const getCountrySelector = state => state.common.country;

const getCCAndCountryProps = (costcenter, country, countries) => {
  return setDefaultSource(country, costcenter, countries);
};

export const getCCAndCountry = createSelector(
  getCostCenterSelector,
  getCountrySelector,
  getFilterCountries,
  getCCAndCountryProps
);

const mapStateToProps = state => {
  const selectedCC = getCCAndCountry(state);
  return {
    companyCode: selectedCC.costcenter,
    dynamicFilters: state.common.dynamicFilters,
  };
};

export default connect(
  mapStateToProps,
  null
)(FilterGroup);



// WEBPACK FOOTER //
// ./src/components/filter/FilterGroup.js